import Vue from "vue";
import Component from "vue-class-component";

import EventBus, { ACTIONS } from "@/event-bus";
import CurrencyApi from "@/api/currency";

import accounting from "accounting";

import BN from "bn.js";
import { fromWei, toWei, toBN, unitMap } from "@/lib/web3x-es/utils";

@Component
export default class CurrencyMixin extends Vue {
  private currencyKey = "USD";

  protected created() {
    EventBus.$on(ACTIONS.CURRENCY_UPDATE, this.changeCurrency);
  }

  get currency() {
    return CurrencyApi.getCurrencyMap()[this.currencyKey];
  }

  /**
   * Prints a value following currency configured parameters
   * @param amount value to print
   */
  protected formatNumber(amount: number, showDecimals = true): string {
    const format = "%v";

    const symbol = this.currency.symbol;
    const decimals = this.currency.decimals;

    return accounting.formatMoney(amount, {
      symbol,
      precision: showDecimals ? this.getPrecision(amount, decimals) : 0,
      decimal: ".",
      thousand: ",",
      format,
    });
  }

  /**
   * Prints a value following currency configured parameters
   * @param amount value to print
   * @param key currency key to get parameters
   */
  protected printCurrency(amount: number, showDecimals = true): string {
    const format = "%s%v";

    const price = this.currency.price;
    const symbol = this.currency.symbol;
    const decimals = showDecimals ? this.currency.decimals : 0;

    const convertedAmount = amount * price;

    return accounting.formatMoney(convertedAmount, {
      symbol,
      precision: this.getPrecision(convertedAmount, decimals),
      decimal: ".",
      thousand: ",",
      format,
    });
  }

  /**
   * Display a percent with + or -, and %
   * @param percentage to display
   */
  protected printPercentage(percentage: number, showSign = true): string {
    if (isFinite(percentage)) {
      const perc = Number(percentage) * 100;
      let prefixChar = "";
      if (showSign) {
        prefixChar = perc >= 0.01 ? "+" : perc > 0 ? "~" : "";
      }
      const printedValue = `${prefixChar}${perc.toFixed(2)}%`;
      return percentage ? printedValue : "0.00%";
    } else {
      return "--%";
    }
  }

  protected getCurrencyKey(): string {
    return this.currencyKey;
  }

  protected getTokenInfo(tokenId: number) {
    return CurrencyApi.getTokensMap()[tokenId] || {};
  }

  protected forceUpdate() {
    CurrencyApi.forceUpdate();
  }

  private getPrecision(amount: number, def: number): number {
    return amount < 1 && amount > -1 ? def + 1 : def;
  }

  private changeCurrency(newCurrencyKey: string) {
    this.currencyKey = newCurrencyKey;
  }

  protected valueFor(marketCap: any, amount: any, supply: any) {
    return (
      Number(marketCap) *
      (1 - (1 - Number(amount) / Number(supply)) ** (1 / (333333 / 1000000)))
    );
  }

  protected currentValueFor(token: any, tokenStats: any) {
    return this.valueFor(tokenStats.marketCap, token.amount, tokenStats.supply);
  }

  protected last24hValueFor(token: any) {
    const tokenLast24h = token.last24h;
    console.debug("last24hValueFor()", tokenLast24h);
    return this.valueFor(
      tokenLast24h.totalMarketCap,
      tokenLast24h.holdings,
      tokenLast24h.totalSupply
    );
  }

  protected toWeiBN(num: string, weiUnit: string) {
    const castedWeiUnit = weiUnit as keyof typeof unitMap;
    return toBN(toWei(num, castedWeiUnit));
  }

  protected fromWeiBN(num: BN, weiUnit: string) {
    const castedWeiUnit = weiUnit as keyof typeof unitMap;
    return fromWei(num.toString(), castedWeiUnit);
  }

  protected convertWei(amount: BN, originalUnit: string, newUnit: string) {
    const parsedAmount = this.fromWeiBN(amount, originalUnit);
    try {
      return this.toWeiBN(parsedAmount, newUnit);
    } catch (error) {
      // Too many decimals for the conversion
      const trimmedAmount = parsedAmount.slice(0, -12);
      return this.toWeiBN(trimmedAmount, newUnit);
    }
  }
}
